import type { TFunction } from "i18next";

import { formatConditionBadge } from "./conditionBadge";
import { getRuleLabel } from "./labels";
import type { FormCondition, FormRule } from "./schemas";

import type { SegmentField } from "@/features/segments/types";

export type ConditionChange = {
  ruleIndex: number;
  before?: FormCondition;
  after?: FormCondition;
  isRuleRemoved?: boolean;
};

export type RuleChangeChip = {
  key: string;
  ruleLabel: string;
  before?: string;
  after?: string;
};

const isSameValues = (a: string[], b: string[]) =>
  a.length === b.length && a.every((value, index) => value === b[index]);

const isSameCondition = (a: FormCondition, b: FormCondition) =>
  a.field === b.field &&
  a.operator === b.operator &&
  isSameValues(a.values, b.values) &&
  a.isInverted === b.isInverted;

const isComplete = (condition: FormCondition) =>
  Boolean(condition.field && condition.operator);

export const diffConditions = (
  saved: FormRule[],
  current: FormRule[],
): ConditionChange[] => {
  const savedById = new Map(
    saved.flatMap((rule, ruleIndex) =>
      rule.conditions.map(
        (condition) => [condition.id, { condition, ruleIndex }] as const,
      ),
    ),
  );
  const currentIds = new Set(
    current.flatMap((rule) => rule.conditions.map((condition) => condition.id)),
  );

  const changes: ConditionChange[] = current.flatMap((rule, ruleIndex) =>
    rule.conditions.filter(isComplete).flatMap((after) => {
      const before = after.id ? savedById.get(after.id)?.condition : undefined;
      if (!before) return [{ ruleIndex, after }];
      return isSameCondition(before, after)
        ? []
        : [{ ruleIndex, before, after }];
    }),
  );

  const currentRuleIndexById = new Map(
    current.map((rule, index) => [rule.id, index]),
  );
  const removed = saved.flatMap((rule, savedRuleIndex): ConditionChange[] => {
    const currentRuleIndex = currentRuleIndexById.get(rule.id);
    return rule.conditions
      .filter((condition) => !currentIds.has(condition.id))
      .map((before) =>
        currentRuleIndex === undefined
          ? { ruleIndex: savedRuleIndex, before, isRuleRemoved: true }
          : { ruleIndex: currentRuleIndex, before },
      );
  });

  return [...changes, ...removed];
};

export const describeCondition = (
  condition: FormCondition,
  fields: SegmentField[],
  t: TFunction,
): string => {
  const parts = formatConditionBadge(condition, fields, t);
  if (!parts) return "";
  return [parts.field, parts.operator, parts.value].filter(Boolean).join(" ");
};

const describeOptional = (
  condition: FormCondition | undefined,
  fields: SegmentField[],
  t: TFunction,
) => (condition ? describeCondition(condition, fields, t) : undefined);

export const toRuleChangeChips = (
  changes: ConditionChange[],
  fields: SegmentField[],
  t: TFunction,
): RuleChangeChip[] =>
  changes.map((change, index) => ({
    key: `${change.ruleIndex}-${index}`,
    ruleLabel: getRuleLabel(t, change.ruleIndex),
    before: describeOptional(change.before, fields, t),
    after: describeOptional(change.after, fields, t),
  }));

export const getEditedHints = (
  changes: ConditionChange[],
  fields: SegmentField[],
  t: TFunction,
): Map<number, string> => {
  const hints = new Map<number, string>();
  for (const change of changes) {
    const before = describeOptional(change.before, fields, t);
    if (change.isRuleRemoved || !before || hints.has(change.ruleIndex)) continue;
    hints.set(
      change.ruleIndex,
      t("admin.segments.builder.exceptions.editedHint", { before }),
    );
  }
  return hints;
};

export const getAffectedText = (
  { exclusions, inclusions }: { exclusions: number; inclusions: number },
  t: TFunction,
  format: (value: number) => string,
): string => {
  const parts: string[] = [];
  if (exclusions > 0) {
    parts.push(
      t("admin.segments.builder.exceptions.exclusionsCount", {
        count: exclusions,
        formatted: format(exclusions),
      }),
    );
  }
  if (inclusions > 0) {
    parts.push(
      t("admin.segments.builder.exceptions.inclusionsCount", {
        count: inclusions,
        formatted: format(inclusions),
      }),
    );
  }
  return parts.join(t("admin.segments.builder.exceptions.and"));
};

export const getChangeSummary = (
  ruleChanges: RuleChangeChip[],
  affectedText: string,
  t: TFunction,
): string => {
  const [onlyChange] = ruleChanges;
  if (ruleChanges.length !== 1 || !onlyChange?.before || !onlyChange.after) {
    return t("admin.segments.builder.exceptions.bannerChanges", {
      affected: affectedText,
    });
  }
  return t("admin.segments.builder.exceptions.bannerSingleChange", {
    before: onlyChange.before,
    after: onlyChange.after,
    rule: onlyChange.ruleLabel,
    affected: affectedText,
  });
};
